import React, { useState, useRef } from 'react';
import { Avatar, Badge, Icon } from 'antd';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const Container = styled.div`
  position: relative;
  display: inline-block;
  z-index: 100;
`;

const AvatarButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 45px;
  right: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const DropdownHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
  color: #666;
  background: #fafafa;
`;

const UserOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  ${props => props.selected && `
    background-color: #e6f7ff;
    border-left: 3px solid #1890ff;
  `}
  
  span {
    font-size: 12px;
  }
`;

const UserSelector = ({ selectedUserId, onUserChange, placeholder = "Filtrar por usuário" }) => {
  const users = useSelector(state => state.users || []);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  
  // Filter out support user
  const filteredUsers = users.filter(user => user.email !== 'suporte@contlabs.com.br');

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  };

  // Get user avatar color based on ID
  const getAvatarColor = (userId) => {
    const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#ff7875', '#40a9ff'];
    return colors[userId % colors.length];
  };

  const handleUserSelect = (value) => {
    onUserChange(value);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (hasFilter) {
      // If there's an active filter, clear it instead of opening dropdown
      onUserChange(null);
    } else {
      // If no filter, open dropdown to select a user
      setIsOpen(!isOpen);
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get current selected user for display
  const selectedUser = selectedUserId ? filteredUsers.find(user => user.id === selectedUserId) : null;
  const hasFilter = selectedUserId !== null;

  const renderUserOption = (user, isSelected = false) => (
    <UserOption 
      key={user.id} 
      selected={isSelected}
      onClick={() => handleUserSelect(user.id)}
    >
      <Avatar
        size="small"
        style={{ 
          backgroundColor: getAvatarColor(user.id),
          color: '#fff',
          fontSize: '12px'
        }}
      >
        {getUserInitials(user.name)}
      </Avatar>
      <span>{user.name}</span>
    </UserOption>
  );

  return (
    <Container ref={containerRef}>
      <AvatarButton onClick={toggleDropdown}>
        {hasFilter && (
          <Badge dot color="#1890ff">
            <Avatar
              size={32}
              style={{ 
                backgroundColor: selectedUser ? getAvatarColor(selectedUser.id) : '#d9d9d9',
                color: '#fff',
                fontSize: '14px',
                border: '2px solid #fff',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              {selectedUser ? getUserInitials(selectedUser.name) : 'ALL'}
            </Avatar>
          </Badge>
        )}
        {!hasFilter && (
          <Avatar
            size={32}
            style={{ 
              backgroundColor: '#d9d9d9',
              color: '#666',
              fontSize: '14px',
              border: '2px solid #fff',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Icon type="team" style={{ fontSize: '16px' }} />
          </Avatar>
        )}
      </AvatarButton>
      
      <Dropdown visible={isOpen}>
        <DropdownHeader>Filtrar por usuário</DropdownHeader>
        {filteredUsers.map(user => 
          renderUserOption(user, selectedUserId === user.id)
        )}
      </Dropdown>
    </Container>
  );
};

export default UserSelector;